import * as React from "react"
import {GeneralContext} from "../../core/general.context"
import * as yup from "yup"
import {LabelContext} from "../../core/label.context"
import {useForm} from "react-hook-form"
import {yupResolver} from "@hookform/resolvers/yup"
import {CheckoutContext} from "../../core/checkout.context"
import {LoginPopup} from "./login.component"
import {PROJECT_COUNTRY} from "../../core/APIUtils"
import {InputElement} from "../input/input.component";

interface FormValues {
    email: string
    password?: string
    profiling?: boolean
}

export const Authentication = React.forwardRef((props: any, ref) => {
    const [email, setEmail] = React.useState("")
    const { userData } = React.useContext(GeneralContext)
    const { cart } = React.useContext(GeneralContext)
    const { t } = React.useContext(LabelContext)
    const { makeSignUp, setMakeSignUp } = React.useContext(CheckoutContext)

    let requiredError = t("ecommerce.form-messagge.required")
    const getValidationSchema = (makeSignUp: boolean) => {
        return yup.object().shape({
            email: yup
                .string()
                .trim()
                .required(requiredError)
                .test("is-valid-email", "Invalid email address", (value) =>
                    !value ? true : yup.string().email().isValidSync(value)
                ),
            password: makeSignUp
                ? yup
                    .string()
                    .required(requiredError)
                    .test(
                        "min-length",
                        "Password must be at least 6 characters",
                        (value) => !value || value.length >= 6
                    )
                : yup.string().optional(),
            profiling: yup.bool().when({
            is: () => ["DE"].includes(PROJECT_COUNTRY),
            then: yup.bool().oneOf([true, false])
        })
        });
    };

    const validationSchema = getValidationSchema(makeSignUp);

    const { register, formState: { errors }, setValue, trigger, getValues, reset } = useForm<FormValues>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            email: email,
            password: "",
            profiling: false,
        },
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    React.useImperativeHandle(ref, () => ({
        authenticationFormValidate: () => trigger(),
        authenticationFormGetData: () => getValues(),
        authenticationFormReset: () => reset()
    }))

    React.useEffect(() => {
        if(PROJECT_COUNTRY === 'BE'){
            setMakeSignUp(true)
        }
    },[])

    React.useEffect(() => {
        if(cart && cart.customerEmail && cart.customerEmail !== ""){
            setEmail(cart.customerEmail)
        }
    }, [cart]);

    React.useEffect(() => {
        setValue("email", email)
    }, [email, setValue]);

    React.useEffect(() => {
        setValue("password", "")
    }, [makeSignUp, setValue])

    React.useEffect(() => {
        getValidationSchema(makeSignUp);
        setValue("password", ""); // Reset the password field if makeSignUp changes
    }, [makeSignUp, setValue]);

    let registrationMessage = t("ecommerce.checkout.registration-title")
    let emailNotificationMessage = t("ecommerce.form-email-notification-message")

    return (
        <div className="checkout-column-main">
            <div className="">
                {window.innerWidth > 992 && (
                    <LoginPopup openPopupDefault={false} subtitle={""}/>
                )}
                {registrationMessage && !userData && <h4 className="mb-2">{registrationMessage}</h4>}
                <form className="checkout-form">

                    {PROJECT_COUNTRY !== 'BE' && (
                        <div className="row py-3">
                            <div className="col-6">
                                <div className="radio-item">
                                    <input
                                        id="makeSignUpNo"
                                        type="radio"
                                        name="makeSignUp"
                                        className="checkout-radio mr-2"
                                        checked={makeSignUp === false}
                                        onChange={() => {setMakeSignUp(false)}}
                                    />
                                    <label htmlFor="makeSignUpNo">
                                        {t("ecommerce.signup.no")}
                                    </label>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="radio-item">
                                <input
                                        id="makeSignUpYes"
                                        type="radio"
                                        name="makeSignUp"
                                        className=""
                                        checked={makeSignUp === true}
                                        onChange={() => {setMakeSignUp(true)}}
                                    />
                                    <label htmlFor="makeSignUpYes">
                                        <div>
                                            {t("ecommerce.signup.yes")}
                                            {window.innerWidth > 992 && (
                                                <div className="extra-info">
                                                    {t("ecommerce.signup.yes.small")}
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <InputElement
                            label={t("ecommerce.signup.email")}
                            placeholder={t("ecommerce.signup.email")}
                            id="email"
                            externalRegister={register("email")}
                            errors={errors.email?.message}
                        />
                    </div>
                    {(emailNotificationMessage && !makeSignUp) &&
                        <div className="extra-info mb-3">
                            {emailNotificationMessage}
                        </div>
                    }
                    {window.innerWidth < 992 && makeSignUp && (
                        <div className="extra-info">
                            {t("ecommerce.signup.yes.small")}
                        </div>
                    )}
                    {makeSignUp && (
                        <>
                            <InputElement
                                type="password"
                                label={t("ecommerce.signup.password")}
                                placeholder={t("ecommerce.signup.password")}
                                id="password"
                                externalRegister={register("password")}
                                errors={errors.password?.message}
                            />
                            {["DE"].includes(PROJECT_COUNTRY) &&
                                <div className="checkboxButton">
                                    <>
                                        <input type="checkbox" value="1" {...register("profiling")} name="profiling" id="profiling" />{" "}
                                        <label
                                            htmlFor="profiling"
                                            dangerouslySetInnerHTML={{ __html: t("ecommerce.signup.profiling-flag") }}
                                        />
                                    </>
                                </div>
                            }
                        </>
                    )}
                </form>
            </div>
        </div>
    )
})